import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { WithholdingService } from '../../../services/withholding.service';
import { AuthService } from '../../../services/auth.service';
import { WithholdingRule, SupplierWithholdingDefaults as Defaults } from '../../../interfaces/procurement/withholding.interface';
import SearchableSelect from '../../common/SearchableSelect';

interface Props {
  value: Defaults;
  onChange: (v: Defaults) => void;
  /** Reports whether the institution is a withholding entity (the section hides itself otherwise). */
  onAvailability?: (available: boolean) => void;
}

/** Default VDS / TDS rule pickers + exemption note for the supplier form. Hidden for non-withholding institutions. */
export const SupplierWithholdingDefaults: React.FC<Props> = ({ value, onChange, onAvailability }) => {
  const institutionId = AuthService.getSelectedInstitution()!;
  const [available, setAvailable] = useState(false);
  const [rules, setRules] = useState<WithholdingRule[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await WithholdingService.getSettings(institutionId);
        if (!alive || !s?.withholdingEntity) return;
        const r = await WithholdingService.listRules(institutionId);
        if (!alive) return;
        setRules((r ?? []).filter(x => x.active));
        setAvailable(true);
        onAvailability?.(true);
      } catch {
        // Not permitted / not configured: no withholding defaults on this form.
      }
    })();
    return () => { alive = false; };
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!available) return null;
  const label = (r: WithholdingRule) => `${r.code} - ${r.ratePct}%`;
  return (
    <Box data-help-section="procurement.supplierForm.section.withholding" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="supplier-withholding-defaults">
      <Typography variant="subtitle1">Withholding at source (when this supplier is paid)</Typography>
      <SearchableSelect<WithholdingRule>
        options={rules.filter(r => r.type === 'VDS')} value={value.defaultVdsRuleId ?? ''}
        onChange={id => onChange({ ...value, defaultVdsRuleId: id || null })}
        data-help="procurement.supplierForm.field.vdsRule"
        getOptionLabel={label} label="Default VDS rule" size="medium"
      />
      <SearchableSelect<WithholdingRule>
        options={rules.filter(r => r.type === 'TDS')} value={value.defaultTdsRuleId ?? ''}
        onChange={id => onChange({ ...value, defaultTdsRuleId: id || null })}
        data-help="procurement.supplierForm.field.tdsRule"
        getOptionLabel={label} label="Default TDS rule" size="medium"
      />
      <TextField
        data-help="procurement.supplierForm.field.exemption"
        label="Exemption note (e.g. certificate of exemption no.)"
        value={value.exemptionNote ?? ''}
        onChange={e => onChange({ ...value, exemptionNote: e.target.value })}
        helperText="When set, no withholding is proposed for this supplier."
      />
    </Box>
  );
};

export default SupplierWithholdingDefaults;
